import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ReferenceLine
} from 'recharts';
import { Sparkles, TrendingUp, AlertCircle } from 'lucide-react';

/**
 * V10 vs V9 prediction chart.
 *
 * V9 actual data: Apr 20 2026 8:01 PM 3rd-gear pull, IAT 85F, sealed intake.
 * V10 prediction model:
 *   - Light load cells (4000-5000 RPM): unchanged
 *   - Rows 10-13 (5500-6500 RPM): +10% commanded fuel.
 *     Stock fuel system has headroom here, so ~95% delivery.
 *     Predicted AFR = V9_AFR / (1 + 0.10 * 0.95) = V9_AFR / 1.095
 *   - Rows 14-15 (6500-7000 RPM): +30% commanded fuel.
 *     Stock pump/injectors are at 90-100% duty at this RPM, so only
 *     ~50-65% of the commanded +30% is actually delivered.
 *     Predicted AFR band: V9_AFR / 1.15  (low bound) to V9_AFR / 1.22 (high bound)
 */
const V9_ACTUAL = [
  { rpm: 4250, afr: 16.67, hp: 165.2 },
  { rpm: 4750, afr: 16.26, hp: 188.3 },
  { rpm: 5250, afr: 14.45, hp: 240.9 },
  { rpm: 5750, afr: 13.96, hp: 289.1 },
  { rpm: 6250, afr: 14.34, hp: 287.9 },
  { rpm: 6750, afr: 14.59, hp: 261.1 },
];

function predictAFR(v9AFR, rpm) {
  if (rpm < 5500) return v9AFR;            // unchanged zones
  if (rpm < 6500) return v9AFR / 1.095;    // rows 10-13 mid-peak
  return v9AFR / 1.18;                     // rows 14-15 (expected delivery ~18%)
}

function predictAFRBand(v9AFR, rpm) {
  if (rpm < 5500) return { low: v9AFR, high: v9AFR };
  if (rpm < 6500) return { low: v9AFR / 1.10, high: v9AFR / 1.09 };
  return { low: v9AFR / 1.22, high: v9AFR / 1.12 }; // uncertainty band for saturation
}

function predictHP(v9HP, rpm) {
  // Power recovery: if V9 was lean (AFR > 13.2 at WOT), V10 recovers HP.
  // Torque improvement is ~1% per 0.3 AFR closer to MBT until ~12.5.
  if (rpm < 5500) return v9HP;
  const v9AFR = V9_ACTUAL.find(d => d.rpm === rpm)?.afr ?? 14.0;
  const wasLean = Math.max(0, v9AFR - 13.0);
  const recovery = Math.min(0.10, wasLean * 0.035); // capped at +10%
  return v9HP * (1 + recovery);
}

export default function V10PredictionChart() {
  const data = useMemo(() => V9_ACTUAL.map(d => {
    const band = predictAFRBand(d.afr, d.rpm);
    const v10AFR = predictAFR(d.afr, d.rpm);
    const v10HP = predictHP(d.hp, d.rpm);
    return {
      rpm: d.rpm,
      v9_afr: d.afr,
      v10_afr: v10AFR,
      v10_afr_low: band.low,
      v10_afr_high: band.high,
      v9_hp: d.hp,
      v10_hp: v10HP,
      hp_gain: v10HP - d.hp,
    };
  }), []);

  const peakGain = Math.max(...data.map(d => d.hp_gain));
  const topEndGain = data[data.length - 1].hp_gain;

  return (
    <div className="mt-3 p-3 bg-[#050505] border border-emerald-500/20 rounded-sm space-y-3" data-testid="v10-prediction-chart">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-xs font-bold text-emerald-300">V10 TopEndFix — Projected vs V9 Actual</span>
        <span className="ml-auto text-[9px] text-zinc-500 font-['JetBrains_Mono']">
          model: enrichment delta + fuel-system saturation
        </span>
      </div>

      {/* AFR Chart */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-zinc-400 uppercase tracking-wider">AFR vs RPM (WOT)</span>
          <span className="text-[10px] text-zinc-500">Target: 12.0 – 13.0</span>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#1a1a1a" strokeDasharray="2 2" />
            <XAxis dataKey="rpm" stroke="#52525b" tick={{ fontSize: 9 }} />
            <YAxis domain={[11, 17]} stroke="#52525b" tick={{ fontSize: 9 }} width={30} />
            <Tooltip
              contentStyle={{ background: '#0a0a0a', border: '1px solid #27272a', fontSize: 11 }}
              labelStyle={{ color: '#a1a1aa' }}
              formatter={(v, name) => [typeof v === 'number' ? v.toFixed(2) : v, name]}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <ReferenceLine y={12.5} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'E85 target', fill: '#10b981', fontSize: 9 }} />
            <ReferenceLine y={14.7} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'stoich', fill: '#f59e0b', fontSize: 9 }} />
            <Area type="monotone" dataKey="v10_afr_high" stroke="none" fill="#10b981" fillOpacity={0.08} name="V10 band" />
            <Area type="monotone" dataKey="v10_afr_low"  stroke="none" fill="#050505" fillOpacity={1} />
            <Line type="monotone" dataKey="v9_afr"  stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="V9 actual" />
            <Line type="monotone" dataKey="v10_afr" stroke="#10b981" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} name="V10 predicted" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* HP Chart */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-zinc-400 uppercase tracking-wider">HP vs RPM</span>
          <span className="text-[10px] text-emerald-400">
            <TrendingUp className="w-3 h-3 inline mr-0.5" />
            peak gain +{peakGain.toFixed(1)} HP • top-end +{topEndGain.toFixed(1)} HP
          </span>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#1a1a1a" strokeDasharray="2 2" />
            <XAxis dataKey="rpm" stroke="#52525b" tick={{ fontSize: 9 }} />
            <YAxis domain={[150, 310]} stroke="#52525b" tick={{ fontSize: 9 }} width={30} />
            <Tooltip
              contentStyle={{ background: '#0a0a0a', border: '1px solid #27272a', fontSize: 11 }}
              labelStyle={{ color: '#a1a1aa' }}
              formatter={(v) => [typeof v === 'number' ? v.toFixed(1) + ' HP' : v, '']}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <ReferenceLine y={285} stroke="#FF5E1A" strokeDasharray="3 3" label={{ value: '285 HP target', fill: '#FF5E1A', fontSize: 9 }} />
            <Line type="monotone" dataKey="v9_hp"  stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="V9 actual" />
            <Line type="monotone" dataKey="v10_hp" stroke="#10b981" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} name="V10 projected" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-start gap-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded-sm">
        <AlertCircle className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-[10px] text-amber-200/80 leading-relaxed">
          <strong>Prediction basis:</strong> V9 actual curve from Apr 20 8:01 PM log (IAT 85°F, sealed intake).
          V10 projection assumes stock fuel system delivers ~95% of commanded enrichment at 5500–6500 RPM
          and only ~55–65% at 6500–7000 RPM (duty-cycle saturation). Real results depend on fuel pressure
          stability; band shows uncertainty. If your car has upgraded injectors, V10 will over-enrich the top-end and need tuning back.
        </p>
      </div>
    </div>
  );
}
